import "./AddCreativeModal.scss";
import { ABCDCompanyOptions } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCompanyOptions";
import { Button, ButtonType, DatePicker, Dropdown, DropdownToggleType } from "../../Components";
import { FieldConfig } from "./AddCreativeConfigsUtils";
import { Form, Modal } from "react-bootstrap";
import { getMediatypeConfig } from "./AddCreativeConfigs";
import { MediaType, useCreativeMap } from "../../redux/creative";
import { useCompanyInfo } from "../../redux/company";
import { useSetAreYouSure, useSetError } from "../../redux/modals";
import * as R from "ramda";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MdCheckBox,
  MdCircle,
  MdClose,
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
  MdOutlineCircle,
  MdOutlineSquare,
} from "react-icons/md";
import { CREATIVE_MEDIATYPES_TYPES } from "../Tabs/CreativeListUtils";

interface AddCreativeModalProps {
  abcdCompanyOptions: ABCDCompanyOptions;
  mediatype: string;
  onHide: () => void;
}

const AddCreativeModal = ({
  abcdCompanyOptions,
  mediatype,
  onHide,
}: AddCreativeModalProps): JSX.Element => {
  const [assetDetails, setAssetDetails] = useState<Record<string, any>>({});
  const [creativeCode, setCreativeCode] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [creativeName, setCreativeNameRaw] = useState<string>("");
  const [editingCreativeNames, setEditingCreativeNames] = useState<boolean>(false);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [duplicateCreativeName, setDuplicateCreativeName] = useState<string>("");
  const [creativeIDOverrideMap, setCreativeIDOverrideMap] = useState<Record<string, string>>({});
  const [creativeNameOverrideMap, setCreativeNameOverrideMap] = useState<Record<string, string>>(
    {}
  );
  const [customAttributesFields, setCustomAttributesFields] = useState<
    { fieldName: any; config: any }[]
  >();
  const [skipMap, setSkipMap] = useState<Record<string, boolean>>({});
  const [version, setVersion] = useState<string>("");

  const companyInfo = useCompanyInfo();
  const TVADCreativeMap = useCreativeMap({
    company: companyInfo.cid,
    mediaTypes: [...(R.defaultTo([], R.keys(assetDetails?.mediatypes)) as MediaType[])],
  });
  const currentMediatypeConfig = useMemo(() => getMediatypeConfig(mediatype), [mediatype]);
  const selectedMediatypes = useMemo(
    () => (assetDetails && assetDetails.mediatypes ? R.keys(assetDetails.mediatypes) : []),
    [assetDetails]
  );
  const creativeMap = useMemo(
    () =>
      selectedMediatypes.includes("audio") ||
      selectedMediatypes.includes("display") ||
      selectedMediatypes.includes("linear") ||
      selectedMediatypes.includes("streaming")
        ? TVADCreativeMap
        : {},
    [selectedMediatypes, TVADCreativeMap]
  );

  const generatedCreatives = useMemo(() => {
    return currentMediatypeConfig?.generateCreatives?.({
      assetDetails,
      companyInfo,
      creativeCode,
      creativeName,
      version,
      creativeMap,
    });
  }, [
    assetDetails,
    companyInfo,
    creativeCode,
    creativeMap,
    creativeName,
    currentMediatypeConfig,
    version,
  ]);

  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure(true);

  const setCreativeName = useCallback((newName: string) => {
    let abbreviation = "";
    const words = newName.split(" ").filter(word => word.match(/^[a-zA-Z]/));
    if (words.length >= 3) {
      for (let i = 0; i < 3; ++i) {
        abbreviation = `${abbreviation}${words[i].charAt(0).toUpperCase()}`;
      }
    } else {
      abbreviation = newName
        .replace(/[^a-zA-Z]/g, "")
        .substring(0, 3)
        .toUpperCase()
        .padEnd(3, "X");
    }
    setCreativeCode(abbreviation);
    setCreativeNameRaw(newName);
  }, []);

  const handleInputChange = useCallback((name: string, value: any) => {
    setAssetDetails(prevDetails => ({
      ...prevDetails,
      [name]: value,
    }));
  }, []);

  const renderField = useCallback(
    (fieldName: string, fieldConfig: FieldConfig) => {
      const currentValue = assetDetails[fieldName];
      const expanded = expandedMap[fieldName] || !fieldConfig.collapsable;
      const labelContainer = (
        <div
          className="groupLabel"
          onClick={() => setExpandedMap({ ...expandedMap, [fieldName]: !expanded })}
        >
          <div className="label">{R.defaultTo(fieldName, fieldConfig.label)}</div>
          {fieldConfig.collapsable && (
            <div className={`collapseIcon ${expanded ? "expanded" : "collapsed"}`}>
              {expanded ? <MdOutlineArrowForwardIos /> : <MdOutlineArrowBackIos />}
            </div>
          )}
        </div>
      );
      switch (fieldConfig?.type) {
        case "radio":
          return (
            <div key={fieldName} className="field-group">
              {labelContainer}
              {expanded && (
                <div className={`radioOptions ${fieldConfig.multiSelect ? "" : "singleSelect"}`}>
                  {fieldConfig.options?.map((option: { value: string; label: string }) => (
                    <div
                      key={option.value}
                      className={`field-check ${
                        assetDetails[fieldName] && assetDetails[fieldName][option.value]
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => {
                        // Get the current state of the specific multi-select field
                        const currentSelectionsMap = assetDetails[fieldName] || {};
                        if (fieldConfig.multiSelect) {
                          // Multi-select (checkbox-like behavior): Add/Remove from map
                          const isCurrentlySelected = currentSelectionsMap[option.value];
                          let newSelectionsMap;
                          if (isCurrentlySelected) {
                            // Deselect: remove the key from the map
                            newSelectionsMap = R.omit([option.value], currentSelectionsMap);
                          } else {
                            // Select: add the key with a true value
                            newSelectionsMap = R.assoc(option.value, true, currentSelectionsMap);
                          }
                          handleInputChange(fieldName, newSelectionsMap);
                        } else if (currentValue !== option.value) {
                          // Single-select (traditional radio button behavior):
                          // Set the field's value directly to the selected option's value
                          // If the option is already selected, clicking it again should do nothing for a typical radio
                          // Only update if it's a new selection
                          handleInputChange(fieldName, { [option.value]: true });
                        }
                      }}
                    >
                      <div className="field-check-input" id={`checkbox-${fieldName}`}>
                        {fieldConfig.multiSelect ? (
                          assetDetails[fieldName] && assetDetails[fieldName][option.value] ? (
                            <MdCheckBox />
                          ) : (
                            <MdOutlineSquare />
                          )
                        ) : assetDetails[fieldName] && assetDetails[fieldName][option.value] ? (
                          <MdCircle />
                        ) : (
                          <MdOutlineCircle />
                        )}
                      </div>
                      <div className="field-check-label">{option.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        case "dropdown":
          return (
            <div key={fieldName} className="field-group">
              {labelContainer}
              {expanded && (
                <div className="dropdownContainer">
                  <Dropdown
                    autofill={true}
                    onChange={option => handleInputChange(fieldName, option)}
                    options={R.defaultTo([], fieldConfig.options)}
                    placeholder={`Select ${fieldName}`}
                    type={DropdownToggleType.OUTLINED}
                    value={currentValue}
                  />
                </div>
              )}
            </div>
          );
        case "dates":
          return (
            <div key={fieldName} className="field-group">
              {labelContainer}
              {expanded && (
                <div className="datePickerContainer">
                  <DatePicker
                    isOutsideRange={date => new Date().getTime() > new Date(date).getTime()}
                    light={true}
                    onChange={dates => handleInputChange(fieldName, dates)}
                    range={assetDetails.dates}
                    type={ButtonType.OUTLINED}
                  />
                </div>
              )}
            </div>
          );
        default:
          // text input
          return (
            <div key={fieldName} className="field-group">
              {labelContainer}
              {expanded && (
                <div className="inputContainer">
                  <Form.Control
                    id={fieldName}
                    onChange={e => handleInputChange(fieldName, e.target.value)}
                    placeholder={fieldConfig?.label || fieldName}
                    type="text"
                    value={assetDetails[fieldName] || ""}
                  />
                </div>
              )}
            </div>
          );
      }
    },
    [assetDetails, expandedMap, handleInputChange]
  );

  const save = useCallback(() => {
    if (currentMediatypeConfig.saveNewCreatives) {
      currentMediatypeConfig.saveNewCreatives({
        assetDetails: { ...assetDetails, creativeName, creativeCode, version },
        company: companyInfo.cid,
        creativeCode,
        creativeIDOverrideMap,
        creativeMap,
        creativeName,
        creativeNameOverrideMap,
        duplicateCreativeName,
        generatedCreatives,
        setAreYouSure,
        setError,
        setSaving,
        skipMap,
        version,
      });
    }
    onHide();
  }, [
    assetDetails,
    companyInfo,
    creativeCode,
    creativeIDOverrideMap,
    creativeMap,
    creativeName,
    creativeNameOverrideMap,
    currentMediatypeConfig,
    duplicateCreativeName,
    generatedCreatives,
    onHide,
    setAreYouSure,
    setError,
    setSaving,
    skipMap,
    version,
  ]);

  const filterAndSortFields = useCallback(
    (fields: Record<string, FieldConfig> | undefined) => {
      if (!fields) {
        return [];
      }
      return R.pipe(
        R.toPairs,
        R.sortBy(args => R.defaultTo(Infinity, R.path([1, "order"], args))),
        R.filter(([fieldName, config]) => {
          const isAvailable = fieldName === "mediatypes" || R.isNil(config.mediatypes);
          if (config.mediatypes && assetDetails && assetDetails.mediatypes) {
            for (const mediatype of config.mediatypes) {
              if (assetDetails.mediatypes[mediatype]) {
                return true;
              }
            }
          }
          return isAvailable;
        }),
        R.map(([fieldName, config]) => ({ fieldName, config }))
      )(fields);
    },
    [assetDetails]
  );

  useEffect(() => {
    (async () => {
      if (
        currentMediatypeConfig &&
        currentMediatypeConfig.getCustomAttributes &&
        R.isNil(customAttributesFields)
      ) {
        setCustomAttributesFields(
          filterAndSortFields(await currentMediatypeConfig.getCustomAttributes(companyInfo.cid))
        );
      }
    })();
  }, [companyInfo, currentMediatypeConfig, customAttributesFields, filterAndSortFields]);

  const detailsFields = useMemo(() => {
    const details =
      currentMediatypeConfig && currentMediatypeConfig.details
        ? currentMediatypeConfig.details
        : {};
    return filterAndSortFields(
      currentMediatypeConfig.updateDetails({
        abcdCompanyOptions,
        assetDetails,
        creativeMap,
        details,
      })
    );
  }, [abcdCompanyOptions, assetDetails, creativeMap, currentMediatypeConfig, filterAndSortFields]);

  const creativeIDsSection = useMemo(() => {
    if (generatedCreatives && currentMediatypeConfig.renderGeneratedCreatives) {
      return currentMediatypeConfig.renderGeneratedCreatives({
        assetDetails,
        companyInfo,
        creativeCode,
        creativeIDOverrideMap,
        creativeNameOverrideMap,
        editingCreativeNames,
        generatedCreatives,
        setCreativeIDOverrideMap,
        setCreativeNameOverrideMap,
        setEditingCreativeNames,
        setSkipMap,
        version,
      });
    }
    return <div />;
  }, [
    assetDetails,
    companyInfo,
    creativeCode,
    creativeIDOverrideMap,
    creativeNameOverrideMap,
    currentMediatypeConfig,
    editingCreativeNames,
    generatedCreatives,
    setCreativeIDOverrideMap,
    setEditingCreativeNames,
    version,
  ]);

  const duplicateIDsSection = useMemo(() => {
    if (generatedCreatives && currentMediatypeConfig.renderDuplicateCreatives) {
      return currentMediatypeConfig.renderDuplicateCreatives({
        assetDetails,
        creativeMap,
        duplicateCreativeName,
        generatedCreatives,
        setDuplicateCreativeName,
      });
    }
    return <div />;
  }, [
    assetDetails,
    creativeMap,
    currentMediatypeConfig,
    duplicateCreativeName,
    generatedCreatives,
    setDuplicateCreativeName,
  ]);

  return (
    <Modal
      show
      onHide={onHide}
      centered
      className={`addCreativeModal ${saving ? "loading" : ""}`}
      size="lg"
    >
      <Modal.Header>
        <div className="modalTitleContainer">
          <Modal.Title>
            Create New Creative{" "}
            {mediatype === CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING ? "/ ISCI(s)" : ""}
          </Modal.Title>
        </div>
        <div className="modalCloseButton" onClick={onHide}>
          <MdClose />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="topSection">
          <div className="top-group creativeName">
            <div className="label">Creative Name:</div>
            <Form.Control
              id="creativeName"
              type="text"
              value={creativeName}
              onChange={e => setCreativeName(e.target.value)}
            />
          </div>
          <div className="top-group creativeCode">
            <div className="label">Creative Code:</div>
            <Form.Control
              id="creativeCode"
              type="text"
              value={creativeCode}
              onChange={e => setCreativeCode(e.target.value)}
            />
          </div>
          <div className="top-group creativeVersion">
            <div className="label">Version:</div>
            <Form.Control
              id="version"
              type="text"
              value={version}
              onChange={e => setVersion(e.target.value)}
            />
          </div>
        </div>
        <div className="border" />
        <div className="fieldSection">
          {detailsFields.length > 0 && (
            <div className="detailsSection">
              <div className="sectionHeader">Details</div>
              <div className="detailsContainer">
                {R.map(({ fieldName, config }) => renderField(fieldName, config), detailsFields)}
              </div>
            </div>
          )}
          <div className="border" />
          {customAttributesFields && customAttributesFields.length > 0 && (
            <>
              <div className="customAttributesSection">
                <div className="sectionHeader">Custom Attributes</div>
                <div className="customAttributesContainer">
                  {R.map(
                    ({ fieldName, config }) => renderField(fieldName, config),
                    customAttributesFields
                  )}
                </div>
              </div>
              <div className="border" />
            </>
          )}
          {creativeIDsSection}
          {duplicateIDsSection}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type={ButtonType.OUTLINED} onClick={onHide}>
          Cancel
        </Button>
        <Button type={ButtonType.FILLED} onClick={save}>
          Create Creatives
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddCreativeModal;
